import { useState } from 'react'
import { Card } from './ui/Card'
import { Button } from './ui/Button'
import { Upload, Image, FileJson, Copy, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'

export const PinataUploadManager = () => {
  const [images, setImages] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadedCIDs, setUploadedCIDs] = useState<string[]>([])
  
  // Загрузка картинок
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      setImages(files)
      toast.success(`${files.length} images loaded`)
    }
  }
  
  // Загрузка на Pinata
  const uploadToPinata = async () => {
    if (images.length === 0) {
      toast.error('Please select images first')
      return
    }
    
    // ⚠️ Для работы нужен Pinata API Key
    const PINATA_JWT = prompt('Enter your Pinata JWT Token:\n\n(Get it from app.pinata.cloud → API Keys)')
    
    if (!PINATA_JWT) {
      toast.error('Pinata JWT required')
      return
    }
    
    // Базовая валидация JWT
    if (!PINATA_JWT.startsWith('eyJ')) {
      toast.error('Invalid JWT format. Should start with "eyJ"')
      return
    }
    
    if (PINATA_JWT.length < 100) {
      toast.error('JWT too short. Make sure you copied the full token')
      return
    }
    
    setUploading(true)
    const cids: string[] = []
    
    try {
      // Шаг 1: Загружаем картинки
      toast('Uploading images to IPFS...', { icon: '📤' })
      
      for (let i = 0; i < images.length; i++) {
        const formData = new FormData()
        formData.append('file', images[i], images[i].name) // Добавляем имя файла
        
        try {
          const response = await axios.post(
            'https://api.pinata.cloud/pinning/pinFileToIPFS',
            formData,
            {
              headers: {
                'Authorization': `Bearer ${PINATA_JWT}`,
                // НЕ устанавливаем Content-Type вручную - браузер сам установит с boundary
              },
            }
          )
          
          if (!response.data?.IpfsHash) {
            throw new Error(`No CID returned for image ${i + 1}`)
          }
          
          cids.push(response.data.IpfsHash)
          toast(`✅ Image ${i + 1}/${images.length} uploaded: ${images[i].name}`, { icon: '✅', duration: 3000 })
        } catch (imageError: any) {
          console.error(`Failed to upload image ${i + 1}:`, imageError)
          const errorMsg = imageError.response?.data?.error || imageError.message || 'Unknown error'
          toast.error(`❌ Failed to upload ${images[i].name}: ${errorMsg}`, { duration: 5000 })
          throw new Error(`Image upload failed: ${images[i].name} - ${errorMsg}`)
        }
      }
      
      if (cids.length === 0) {
        throw new Error('No images were uploaded successfully')
      }
      
      setUploadedCIDs(cids)
      toast.success(`✅ All ${cids.length} images uploaded successfully!`, { duration: 3000 })
      
      // Шаг 2: Генерируем JSON метаданные
      toast('Generating metadata...', { icon: '🤖' })
      
      const metadataFiles = cids.map((cid, index) => {
        const tokenId = index + 1
        return {
          name: `${tokenId}.json`,
          content: {
            name: `My NFT #${tokenId}`,
            description: `NFT number ${tokenId} in my collection`,
            image: `ipfs://${cid}`,
            attributes: [
              {
                trait_type: 'Edition',
                value: tokenId,
              },
              {
                trait_type: 'Rarity',
                value: tokenId <= 10 ? 'Rare' : 'Common',
              },
            ],
          },
        }
      })
      
      // Шаг 3: Загружаем JSON на Pinata
      toast('Uploading metadata files...', { icon: '📁' })
      
      const jsonCIDs: string[] = []
      for (let i = 0; i < metadataFiles.length; i++) {
        const file = metadataFiles[i]
        const blob = new Blob([JSON.stringify(file.content, null, 2)], {
          type: 'application/json',
        })
        const formData = new FormData()
        formData.append('file', blob, file.name)
        
        try {
          const response = await axios.post(
            'https://api.pinata.cloud/pinning/pinFileToIPFS',
            formData,
            {
              headers: {
                'Authorization': `Bearer ${PINATA_JWT}`,
              },
            }
          )
          
          if (!response.data?.IpfsHash) {
            throw new Error(`No CID returned for JSON ${i + 1}`)
          }
          
          jsonCIDs.push(response.data.IpfsHash)
          toast(`✅ Metadata ${i + 1}/${metadataFiles.length} uploaded`, { icon: '📄', duration: 2000 })
        } catch (jsonError: any) {
          console.error(`Failed to upload JSON ${i + 1}:`, jsonError)
          const errorMsg = jsonError.response?.data?.error || jsonError.message || 'Unknown error'
          toast.error(`❌ Failed to upload ${file.name}: ${errorMsg}`, { duration: 5000 })
          throw new Error(`JSON upload failed: ${file.name} - ${errorMsg}`)
        }
      }
      
      if (jsonCIDs.length === 0) {
        throw new Error('No JSON files were uploaded successfully')
      }
      
      toast.success('🎉 All files uploaded to IPFS!', { duration: 5000 })
      
      // Показываем детальную информацию
      const summary = `
✅ Upload Complete!

📸 Images Uploaded: ${cids.length}
${cids.map((cid, i) => `   ${i + 1}. ${images[i]?.name || `${i + 1}.jpg`} → ${cid}`).join('\n')}

📄 JSON Metadata Uploaded: ${jsonCIDs.length}
${jsonCIDs.map((cid, i) => `   ${i + 1}.json → ${cid}`).join('\n')}

⚠️ IMPORTANT: 
You need to create a FOLDER on Pinata and upload all JSON files there to get a folder CID for Base URI.

Or use the script: PINATA_JWT=... node scripts/upload-to-pinata.js
      `.trim()
      
      alert(summary)
      
    } catch (error: any) {
      console.error('Upload error:', error)
      
      // Определяем текст ошибки
      let errorMessage = 'Upload failed'
      
      if (error.response?.status === 401) {
        errorMessage = '❌ Invalid Pinata JWT. Check your API key!'
      } else if (error.response?.data?.error) {
        errorMessage = String(error.response.data.error)
      } else if (error.message) {
        errorMessage = String(error.message)
      }
      
      toast.error(errorMessage, { duration: 5000 })
    } finally {
      setUploading(false)
    }
  }
  
  // Копирование CID
  const copyCID = (cid: string) => {
    navigator.clipboard.writeText(cid)
    toast.success('CID copied!')
  }
  
  return (
    <Card>
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Upload className="w-5 h-5" />
        Pinata Upload Manager (Beta)
      </h2>
      
      {/* Warning */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
        <div className="flex gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
          <div className="text-sm text-yellow-200">
            <p className="font-bold mb-1">⚠️ Рекомендуется для малых коллекций (&lt;100 NFT)</p>
            <p>Для больших коллекций (1,000+) используйте Pinata.cloud напрямую.</p>
          </div>
        </div>
      </div>
      
      {/* Upload Images */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            <Image className="w-4 h-4 inline mr-2" />
            Upload Images
          </label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageUpload}
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white hover:file:bg-purple-700"
          />
          {images.length > 0 && (
            <p className="text-sm text-slate-400 mt-2">
              ✅ {images.length} images selected
            </p>
          )}
        </div>
        
        {/* Upload Button */}
        <Button
          onClick={uploadToPinata}
          disabled={uploading || images.length === 0}
          className="w-full"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload to Pinata & Generate Metadata
            </>
          )}
        </Button>
        
        {/* Results */}
        {uploadedCIDs.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-bold text-white">✅ Uploaded Images ({uploadedCIDs.length}):</h3>
            <div className="bg-slate-800/50 rounded-lg p-4 max-h-60 overflow-y-auto">
              {uploadedCIDs.map((cid, index) => (
                <div key={cid} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                  <div className="flex flex-col">
                    <span className="text-sm text-slate-300">
                      {images[index]?.name || `${index + 1}.jpg`}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      CID: {cid}
                    </span>
                    <a 
                      href={`https://gateway.pinata.cloud/ipfs/${cid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-purple-400 hover:text-purple-300 mt-1"
                    >
                      View on IPFS →
                    </a>
                  </div>
                  <button
                    onClick={() => copyCID(cid)}
                    className="text-purple-400 hover:text-purple-300 ml-4"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <p className="text-sm text-green-200">
                💡 <strong>Tip:</strong> Check your Pinata Files page - images should be there with their original filenames!
              </p>
            </div>
          </div>
        )}
        
        {/* Instructions */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-6">
          <p className="text-sm text-blue-200">
            <FileJson className="w-4 h-4 inline mr-2" />
            <strong>How it works:</strong>
          </p>
          <ol className="text-sm text-blue-200 mt-2 space-y-1 ml-6 list-decimal">
            <li>Upload images → Get CIDs</li>
            <li>Auto-generate JSON metadata</li>
            <li>Upload JSONs to Pinata</li>
            <li>Get folder CID</li>
            <li>Set Base URI in contract</li>
          </ol>
        </div>
      </div>
    </Card>
  )
}
